import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { isValidUrl } from '../utils/validation'
import Forum from '../components/Forum'
import Tutorials from '../components/Tutorials'
import Creations from '../components/Creations'
import './Community.css'

const defaultScheduleItems = [
  {
    id: 'garden-streams',
    title: 'Garden Streams',
    location: 'Twitch Live',
    description: 'Weekly live coding sessions in the garden. Ambient explorations, plant data sonification, and chill vibes.',
    nextSessionUrl: '',
    nextSessionDate: '',
    nextSessionText: '',
  },
  {
    id: 'creative-tech-lab',
    title: 'Creative Tech Lab',
    location: 'Hybrid',
    description:
      'Experimental workshops combining code, sound, and interactive installations. Open to all skill levels.',
    nextSessionUrl: '',
    nextSessionDate: '',
    nextSessionText: '',
  },
  {
    id: 'circuits-vines',
    title: 'Circuits & Vines',
    location: 'Online',
    description: 'Hybrid sets blending live coding with hardware jams. Motion sensors, MIDI controllers, and code.',
    nextSessionUrl: '',
    nextSessionDate: '',
    nextSessionText: '',
  },
]

const Community = () => {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab') as 'forum' | 'tutorials' | 'creations' | 'schedule' | null
  const [activeTab, setActiveTab] = useState<'forum' | 'tutorials' | 'creations' | 'schedule'>(tabParam || 'forum')
  const [scheduleItems, setScheduleItems] = useState(defaultScheduleItems)

  // Sync tab with URL params
  useEffect(() => {
    if (tabParam && ['forum', 'tutorials', 'creations', 'schedule'].includes(tabParam)) {
      setActiveTab(tabParam)
    }
  }, [tabParam])

  // Redirect to home if not logged in
  useEffect(() => {
    if (!user) {
      navigate('/', { replace: true })
    }
  }, [user, navigate])

  // Load schedule items from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('schedule_items')
    if (stored) {
      try {
        setScheduleItems(JSON.parse(stored))
      } catch (e) {
        console.error('Error loading schedule:', e)
      }
    } else {
      localStorage.setItem('schedule_items', JSON.stringify(defaultScheduleItems))
    }
  }, [])

  const handleTabChange = (tab: 'forum' | 'tutorials' | 'creations' | 'schedule') => {
    setActiveTab(tab)
    setSearchParams({ tab })
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  if (!user) {
    return null
  }

  return (
    <main className="main-content community-page">
      <div className="community-header">
        <h1>the signal community</h1>
      </div>

      <div className="community-tabs">
        <button
          className={`community-tab ${activeTab === 'forum' ? 'active' : ''}`}
          onClick={() => handleTabChange('forum')}
        >
          forum
        </button>
        <button
          className={`community-tab ${activeTab === 'tutorials' ? 'active' : ''}`}
          onClick={() => handleTabChange('tutorials')}
        >
          tutorials
        </button>
        <button
          className={`community-tab ${activeTab === 'creations' ? 'active' : ''}`}
          onClick={() => handleTabChange('creations')}
        >
          creations
        </button>
        <button
          className={`community-tab ${activeTab === 'schedule' ? 'active' : ''}`}
          onClick={() => handleTabChange('schedule')}
        >
          schedule
        </button>
      </div>

      <div className="community-content">
        {activeTab === 'forum' && (
          <Forum />
        )}

        {activeTab === 'tutorials' && (
          <Tutorials />
        )}

        {activeTab === 'creations' && (
          <Creations />
        )}

        {activeTab === 'schedule' && (
          <div className="community-tab-content schedule-content">
            <div className="schedule-list">
              {scheduleItems.map((item) => (
                <div key={item.id} className="schedule-item">
                  <div className="schedule-item-header">
                    <h3 className="schedule-item-title">{item.title}</h3>
                    <div className="schedule-item-location">{item.location}</div>
                  </div>
                  <p className="schedule-item-description">{item.description}</p>
                  {(item.nextSessionUrl || item.nextSessionDate) && (
                    <div className="schedule-next-session">
                      <div className="schedule-next-label">
                        {item.nextSessionDate ? 'upcoming session:' : 'recent session:'}
                      </div>
                      {item.nextSessionDate && (
                        <div className="schedule-next-date">{item.nextSessionDate}</div>
                      )}
                      {item.nextSessionUrl && isValidUrl(item.nextSessionUrl) && (
                        <a href={item.nextSessionUrl} target="_blank" rel="noopener noreferrer" className="schedule-next-link">
                          <i className="fas fa-external-link-alt"></i>
                          {item.nextSessionText || (item.nextSessionDate ? 'watch live' : 'watch recording')}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </main>
  )
}

export default Community
